import React, { useState, useEffect } from 'react';
import {
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Box,
  InputAdornment,
  Tooltip
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ListAltIcon from '@mui/icons-material/ListAlt';
import { FaArrowLeft, FaSearch, FaPlus } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { cvManagementApi } from '../../../services/api';
import { Container, Header, HeaderLeft, BackButton, Title, Content, HeaderButton, ControlsContainer } from '../../common/styles';
import { MainContainer, IssuersGrid, IssuerCard, IssuerCardContent, IssuerTitle, ActionButtons } from './styles';
import LoadingSpinner from '../../common/LoadingSpinner';
import EmptyState from '../../common/EmptyState';
import { useAlert } from '../../../contexts/AlertContext';

const CertificationIssuer = () => {
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const [issuers, setIssuers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [currentIssuer, setCurrentIssuer] = useState({ name: '', employeeId: null });
  const [isEditing, setIsEditing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showControls, setShowControls] = useState(true);

  useEffect(() => {
    fetchCertificationIssuers();
  }, []);

  const fetchCertificationIssuers = async () => {
    try {
      setLoading(true);
      const data = await cvManagementApi.getGeneralCertificationIssuers();
      setIssuers(data);
    } catch (err) {
      showAlert('Failed to load certification issuers.', 'error', err);
      console.error('Error fetching certification issuers:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (issuer = null) => {
    if (issuer) {
      setCurrentIssuer(issuer);
      setIsEditing(true);
    } else {
      setCurrentIssuer({ name: '', employeeId: null });
      setIsEditing(false);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleOpenDeleteDialog = (issuer) => {
    setCurrentIssuer(issuer);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCurrentIssuer({
      ...currentIssuer,
      [name]: value
    });
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      if (isEditing) {
        await cvManagementApi.updateCertificationIssuer(currentIssuer.id, {
          name: currentIssuer.name
        });
        showAlert('Certification issuer updated successfully', 'success');
      } else {
        await cvManagementApi.createCertificationIssuer({
          name: currentIssuer.name
        });
        showAlert('Certification issuer created successfully', 'success');
      }
      handleCloseDialog();
      await fetchCertificationIssuers();
    } catch (err) {
      showAlert(err.message || `Failed to ${isEditing ? 'update' : 'create'} certification issuer. Please try again.`, 'error', err);
      console.error(`Error ${isEditing ? 'updating' : 'creating'} certification issuer:`, err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      setLoading(true);
      await cvManagementApi.deleteCertificationIssuer(currentIssuer.id);
      showAlert('Certification issuer deleted successfully', 'success');
      handleCloseDeleteDialog();
      await fetchCertificationIssuers();
    } catch (err) {
      showAlert(err.message || 'Failed to delete certification issuer. Please try again.', 'error', err);
      console.error('Error deleting certification issuer:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredIssuers = issuers.filter(issuer =>
    issuer.name.toLowerCase().includes((searchTerm || '').toLowerCase())
  );

  if (loading && issuers.length === 0) {
    return <LoadingSpinner />;
  }

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <BackButton onClick={() => navigate('/cv-configuration')}>
            <FaArrowLeft /> Back to CV Configuration
          </BackButton>
          <Title>Certification Issuer Configuration</Title>
        </HeaderLeft>
      </Header>

      <Content>
        <ControlsContainer $show={showControls} style={{ marginBottom: '20px' }}>
          <Box sx={{
            display: 'flex',
            gap: '16px',
            flexWrap: 'wrap',
            width: '100%',
            alignItems: 'flex-start',
            justifyContent: 'space-between'
          }}>
            <Box sx={{ display: 'flex', gap: '16px', flexWrap: 'wrap', flex: 1 }}>
              <TextField
                placeholder="Search Certification Issuers"
                variant="outlined"
                size="small"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                autoComplete="off"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <FaSearch style={{ color: '#6B7280' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  flex: 1,
                  maxWidth: '500px',
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'white',
                    height: '40px',
                    fontSize: '14px',
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#2563eb',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#2563eb',
                    }
                  },
                  '& .MuiInputBase-input': {
                    padding: '8px 14px',
                  }
                }}
              />
            </Box>

            <HeaderButton
              onClick={() => handleOpenDialog()}
              sx={{
                minWidth: 'auto',
                height: '32px',
                padding: '0 16px',
                backgroundColor: '#2563eb',
                color: 'white',
                alignSelf: 'flex-start',
                '&:hover': {
                  backgroundColor: '#1d4ed8',
                }
              }}
            >
              <FaPlus style={{ marginRight: '8px' }} /> Add Issuer
            </HeaderButton>
          </Box>
        </ControlsContainer>

        <MainContainer>
          <div style={{
            color: '#64748B',
            fontSize: '14px',
            marginBottom: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: '4px'
          }}>
            <span style={{ fontWeight: 600 }}>
              {filteredIssuers.length}
            </span>
            {filteredIssuers.length === 1 ? 'issuer' : 'issuers'} found
          </div>

          {filteredIssuers.length === 0 ? (
            <EmptyState
              title="No certification issuers found"
              description={searchTerm ?
                "No certification issuers match your search criteria. Try adjusting your search term." :
                "No certification issuers have been added yet. Click the '+ Add Issuer' button to add your first certification issuer."}
            />
          ) : (
            <IssuersGrid>
              {filteredIssuers.map((issuer) => (
                <IssuerCard key={issuer.id}>
                  <IssuerCardContent>
                    <IssuerTitle>
                      {issuer.name}
                    </IssuerTitle>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '13px', mt: 0.5 }}>
                      {issuer.catalogCertifications?.length || 0} {issuer.catalogCertifications?.length === 1 ? 'certification' : 'certifications'}
                    </Typography>

                    <ActionButtons>
                      <Tooltip title="Manage certifications">
                        <IconButton
                          size="small"
                          onClick={() => navigate(`/cv-configuration/certification-issuer/${issuer.id}/catalog`)}
                          sx={{ color: '#2563eb' }}
                        >
                          <ListAltIcon />
                        </IconButton>
                      </Tooltip>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog(issuer)}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDeleteDialog(issuer)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </ActionButtons>
                  </IssuerCardContent>
                </IssuerCard>
              ))}
            </IssuersGrid>
          )}
        </MainContainer>
      </Content>

      {/* Add/Edit Issuer Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{isEditing ? 'Edit Certification Issuer' : 'Add Certification Issuer'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            name="name"
            label="Issuer Name"
            type="text"
            fullWidth
            variant="outlined"
            value={currentIssuer.name}
            onChange={handleInputChange}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="primary"
            disabled={!currentIssuer.name.trim()}
          >
            {isEditing ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Issuer Confirmation Dialog */}
      <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the certification issuer "{currentIssuer.name}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
          <Button onClick={handleDelete} variant="contained" color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default CertificationIssuer;
